import type { TList } from '@/services/api';
import type { TNetworkError } from '@/services/errors';
import type {
  TAssessmentDTO,
  TBookingMemberDTO,
  TCancelEventBodyDTO,
  TCancelEventResponseDTO,
  TConciergeSessionDTO,
  TInterviewAttemptsDTO,
  TInterviewDTO,
  TStaffInterviewDTO,
} from './interviews.dto';

import { BaseService, toSearchFilter } from '@/services/api';
import { errorUtils } from '@/services/errors';
import { withSessionHeaders } from '@/services/supabase';

import type {
  TAssessment,
  TCalendlyCancelOutcome,
  TCompleteConciergeSessionPayload,
  TConciergeSession,
  TInterview,
  TInterviewAttempts,
  TInterviewEnding,
  TReleaseInterviewPayload,
  TSaveAssessmentPayload,
  TStaffInterview,
  TStaffInterviewQuery,
} from './interviews.types';

import {
  ASSESSMENT_FIELDS,
  CANCEL_EVENT_PATH,
  CONCIERGE_SESSION_FIELDS,
  HELD_SESSION_FILTER,
  INTRO_CALL_KIND,
  LIST_INTERVIEW_FIELDS,
  SEARCH_BOOKING_COLUMNS,
  STAFF_INTERVIEW_FIELDS,
} from './interviews.fields';
import {
  toAssessment,
  toConciergeSession,
  toInterview,
  toInterviewAttempts,
  toMemberID,
  toStaffInterview,
} from './interviews.adapters';

class InterviewsService extends BaseService {
  protected readonly serviceName = 'interviewsService';

  public listInterviews(applicantID: string): Promise<TList<TInterview> | TNetworkError> {
    return this.runList(
      this.db
        .from('interview_bookings')
        .select(LIST_INTERVIEW_FIELDS, { count: 'exact' })
        .eq('member_id', applicantID)
        .is('canceled_at', null)
        .order('starts_at', { ascending: false })
        .returns<TInterviewDTO[]>(),
      'listInterviews',
      toInterview,
    );
  }

  private async toSearchedMemberIDs(search?: string): Promise<string[] | null | TNetworkError> {
    if (!search) {
      return null;
    }

    const result = await this.runList(
      this.db
        .from('roster')
        .select('user_id')
        .or(toSearchFilter(SEARCH_BOOKING_COLUMNS, search))
        .returns<TBookingMemberDTO[]>(),
      'listStaffInterviews',
      toMemberID,
    );

    if (errorUtils.isNetworkError(result)) {
      return result;
    }

    return result.rows;
  }

  public async listStaffInterviews(
    query: TStaffInterviewQuery,
  ): Promise<TList<TStaffInterview> | TNetworkError> {
    const { interviewerID, search } = query;

    const searchedIDs = await this.toSearchedMemberIDs(search);

    if (errorUtils.isNetworkError(searchedIDs)) {
      return searchedIDs;
    }

    let builder = this.db
      .from('interview_bookings')
      .select(STAFF_INTERVIEW_FIELDS, { count: 'exact' })
      .is('canceled_at', null);

    if (interviewerID) {
      builder = builder.eq('interviewer_id', interviewerID);
    }

    if (searchedIDs) {
      builder = builder.in('member_id', searchedIDs);
    }

    return this.runList(
      builder.order('starts_at', { ascending: true }).returns<TStaffInterviewDTO[]>(),
      'listStaffInterviews',
      toStaffInterview,
    );
  }

  public saveAssessment({
    bookingID,
    firstPass,
    appearance,
    redFlags,
    communication,
    personality,
    overall,
  }: TSaveAssessmentPayload): Promise<TAssessment | TNetworkError> {
    return this.run(
      this.db
        .from('interview_assessments')
        .insert({
          booking_id: bookingID,
          first_pass: firstPass,
          appearance,
          red_flags: redFlags,
          communication,
          personality,
          overall,
        })
        .select(ASSESSMENT_FIELDS)
        .single()
        .returns<TAssessmentDTO>(),
      'saveAssessment',
      toAssessment,
    );
  }

  public updateAssessment({
    bookingID,
    firstPass,
    appearance,
    redFlags,
    communication,
    personality,
    overall,
  }: TSaveAssessmentPayload): Promise<TAssessment | TNetworkError> {
    return this.run(
      this.db
        .from('interview_assessments')
        .update({
          first_pass: firstPass,
          appearance,
          red_flags: redFlags,
          communication,
          personality,
          overall,
        })
        .eq('booking_id', bookingID)
        .select(ASSESSMENT_FIELDS)
        .single()
        .returns<TAssessmentDTO>(),
      'updateAssessment',
      toAssessment,
    );
  }

  public releaseInterview({ bookingID, outcome }: TReleaseInterviewPayload): Promise<unknown | TNetworkError> {
    return this.run(
      this.db.rpc('release_interview', { p_booking_id: bookingID, p_outcome: outcome }),
      'releaseInterview',
    );
  }

  public completeInterview(bookingID: string): Promise<unknown | TNetworkError> {
    return this.run(
      this.db.rpc('complete_interview', { p_booking_id: bookingID }),
      'completeInterview',
    );
  }

  public listConciergeSessions(memberID: string): Promise<TList<TConciergeSession> | TNetworkError> {
    return this.runList(
      this.db
        .from('interview_bookings')
        .select(CONCIERGE_SESSION_FIELDS, { count: 'exact' })
        .eq('member_id', memberID)
        .eq('kind', INTRO_CALL_KIND)
        .or(HELD_SESSION_FILTER)
        .order('completed_at', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: false })
        .returns<TConciergeSessionDTO[]>(),
      'listConciergeSessions',
      toConciergeSession,
    );
  }

  public completeConciergeSession({ memberID }: TCompleteConciergeSessionPayload): Promise<unknown | TNetworkError> {
    return this.run(
      this.db.rpc('complete_concierge_session', { p_member_id: memberID }),
      'completeConciergeSession',
    );
  }

  public readAttempts(applicantID: string): Promise<TInterviewAttempts | TNetworkError> {
    return this.run(
      this.db
        .rpc('interview_attempts', { p_member_id: applicantID })
        .single()
        .returns<TInterviewAttemptsDTO>(),
      'readAttempts',
      toInterviewAttempts,
    );
  }

  public async cancelEvent(bookingID: string, outcome: TInterviewEnding): Promise<TCalendlyCancelOutcome | TNetworkError> {
    const response = await this.post<TCancelEventResponseDTO, TCancelEventBodyDTO>(
      CANCEL_EVENT_PATH,
      { booking_id: bookingID, outcome },
      { where: 'cancelEvent', headers: await withSessionHeaders() },
    );

    if (errorUtils.isNetworkError(response)) {
      return response;
    }

    return response.calendly;
  }
}

const interviewsService = new InterviewsService();

export {
  interviewsService,
};
